import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { useNotify, useRecordContext, useUpdate } from 'ra-core';
import { useState } from 'react';
import { useVendorApprovers } from './useVendorApprovers';

export const CheckVendorButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [update, { isPending }] = useUpdate();
    const [open, setOpen] = useState(false);
    const [checkedBy, setCheckedBy] = useState('');
    const approvers = useVendorApprovers();

    // Only relevant before final approval / blacklist
    if (!record || ['approved', 'conditional', 'blacklisted', 'checked'].includes(record.status)) return null;

    const handleCheck = () => {
        update(
            'vendors',
            {
                id: record.id,
                data: {
                    status: 'checked',
                    checked_by: checkedBy || null,
                    checked_at: new Date().toISOString(),
                },
                previousData: record,
            },
            {
                onSuccess: () => {
                    notify('Vendor marked as Checked', { type: 'success' });
                    setOpen(false);
                },
                onError: (error: any) => {
                    notify(`Error updating vendor: ${error.message}`, { type: 'error' });
                },
            }
        );
    };

    return (
        <>
            <Button
                onClick={() => setOpen(true)}
                disabled={isPending}
                variant="outline"
                size="sm"
                className="text-cyan-700 border-cyan-200 hover:bg-cyan-50 gap-2"
            >
                <ClipboardCheck className="w-4 h-4" />
                Mark Checked
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Mark Vendor as Checked</DialogTitle>
                        <DialogDescription>
                            Record who verified <span className="font-semibold">{record.name}</span> before approval.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-2 py-2">
                        <Label htmlFor="checked_by">Checked By</Label>
                        <Select value={checkedBy} onValueChange={setCheckedBy}>
                            <SelectTrigger id="checked_by" className="w-full">
                                <SelectValue placeholder="Select reviewer…" />
                            </SelectTrigger>
                            <SelectContent>
                                {approvers.map((a) => (
                                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
                            Cancel
                        </Button>
                        <Button onClick={handleCheck} disabled={isPending} className="gap-2">
                            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
